/**
 * Shared spring/transition presets for the depth system.
 *
 * ONE PLACE FOR "WHAT DOES A PRESS FEEL LIKE"
 * --------------------------------------------
 * `ScreenSlot`, `BottomNav`, `Sheet` and the `lib/motion/*` primitives all want
 * the same handful of feels (a quick tap-back, a soft hover lift, a page
 * transition that settles in ~200ms). Keeping the numbers here means tuning
 * "snappier" once instead of hunting five files for a stray `stiffness: 500`.
 *
 * Every value here is a `transform`/`opacity` transition — nothing in this
 * module ever targets a layout property.
 */
import type { Transition } from "framer-motion";

/** Instant — used whenever `useReducedMotion()` is true. Never omit a
 * transition under reduced motion; swap it for this instead. */
export const SPRING_INSTANT: Transition = { duration: 0 };

/** A quick, tight settle for presses and tab-pill glides (~150ms). */
export const SPRING_SNAPPY: Transition = { type: "spring", stiffness: 500, damping: 32 };

/** A softer settle for hover lifts and small reveals (~220ms). */
export const SPRING_SOFT: Transition = { type: "spring", stiffness: 260, damping: 28 };

/** Screen-to-screen slide/fade — spec calls for "~200ms spring". */
export const SPRING_SCREEN: Transition = { type: "spring", duration: 0.2, bounce: 0.18 };

/** The sheet's slide-up/down open + snap-back-after-drag settle. */
export const SPRING_SHEET: Transition = {
  type: "spring",
  stiffness: 380,
  damping: 34,
  mass: 0.7,
};

/** Picks the real transition or the instant one, given reduced-motion state. */
export function withReducedMotion(transition: Transition, reduceMotion: boolean | null): Transition {
  return reduceMotion ? SPRING_INSTANT : transition;
}
